import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatWidget from '../components/ChatWidget';

const GRADIENT = 'linear-gradient(135deg, #7F77DD, #1D9E75)';
const GRADIENT_TEXT = { background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' };

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function AnimatedSection({ children, delay = 0, style = {} }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(32px)', transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`, ...style }}>
      {children}
    </div>
  );
}

function PulsingDot({ color = '#1D9E75' }) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 10, height: 10 }}>
      <span style={{ position: 'absolute', width: 10, height: 10, borderRadius: '50%', background: color, opacity: 0.3, animation: 'ping 1.5s cubic-bezier(0,0,0.2,1) infinite' }} />
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block' }} />
    </span>
  );
}

const STEPS = [
  { n: '01', title: 'Upload', color: '#7F77DD', desc: 'Upload your leads or connect your scraper. FLO organizes everything and gets ready to work.' },
  { n: '02', title: 'Text', color: '#5BA8DD', desc: 'The AI sends a warm, personalized first text during business hours. Replies get answered in under a minute, any time of day or night.' },
  { n: '03', title: 'Qualify', color: '#3DB88A', desc: 'Claude reads every reply, handles objections naturally, and moves interested leads toward a call — all without you lifting a finger.' },
  { n: '04', title: 'Book', color: '#1D9E75', desc: 'When a lead is ready, FLO books the appointment, sends a confirmation, and hands the conversation off to you. Just show up and close.' },
];

const FEATURES = [
  { icon: '🔍', title: 'Lead Scraper', desc: 'Pro and Pro+ users unlock access to a powerful lead scraper — find self-employed prospects by industry and location, imported directly to your CRM.', badge: 'Pro' },
  { icon: '🧠', title: 'Claude AI Conversations', desc: 'FLO\'s AI runs 24/7 — every reply handled by Claude instantly, any time of day or night. Natural, contextual, never robotic.' },
  { icon: '📅', title: 'Automatic Appointment Booking', desc: 'FLO detects booking intent, creates the appointment in your calendar, sends a confirmation SMS, and stops texting — seamlessly.' },
  { icon: '🕐', title: 'Timezone-Aware Outreach', desc: 'First texts only go out between 8am–8pm in the lead\'s local timezone. Replies get answered fast — outbound texts respect local business hours.' },
  { icon: '📊', title: 'Full Pipeline Analytics', desc: 'Response rates, booking rates, conversion rates, lead sources. Everything you need to see what\'s working.' },
  { icon: '✉️', title: 'Policy Email Workflows', desc: 'After every sale, the right email fires automatically. Individual, Life, Group Health — each policy type gets its own custom template.' },
];

const PLANS = [
  { name: 'Basic', price: 'Free', desc: 'Get started for free. Pay only for what you use.', features: ['Full AI conversations', 'CSV lead upload', 'Appointments & CRM', 'Email templates', 'Analytics', 'CPA Tracker', 'Pay-per-message credits'], cta: 'Start for free', highlight: false },
  { name: 'Pro', price: '$200', desc: 'Everything in Free plus access to the lead scraper.', features: ['Everything in Free', 'Basic lead scraper access (sladeiq.com)', 'Mass texting', 'Workflow builder', 'Split inbox view'], cta: 'Start free trial', highlight: true },
  { name: 'Pro+', price: '$397', desc: 'The complete FLO experience.', features: ['Everything in Pro', 'Pro lead scraper access (sladeiq.com)', 'Priority support', 'Unlimited leads/month'], cta: 'Start free trial', highlight: false },
];

export default function Landing() {
  const navigate = useNavigate();
  const [count, setCount] = useState({ leads: 0, texts: 0, appts: 0 });
  useEffect(() => {
    const targets = { leads: 12847, texts: 94203, appts: 3291 };
    const duration = 2000;
    const steps = 60;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      const progress = step / steps;
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount({
        leads: Math.floor(targets.leads * ease),
        texts: Math.floor(targets.texts * ease),
        appts: Math.floor(targets.appts * ease),
      });
      if (step >= steps) clearInterval(interval);
    }, duration / steps);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#080b0f', color: '#f0efe9', fontFamily: "'DM Sans', 'Outfit', system-ui, sans-serif", overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
        @keyframes ping { 75%,100% { transform: scale(2); opacity: 0; } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes glow { 0%,100% { opacity: 0.4; } 50% { opacity: 0.8; } }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: #7F77DD44; }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: #0d1014; } ::-webkit-scrollbar-thumb { background: #2a2d3a; border-radius: 3px; }
        .btn-primary { background: linear-gradient(135deg,#7F77DD,#1D9E75); border: none; color: #fff; font-weight: 700; cursor: pointer; transition: all 0.2s; letter-spacing: -0.3px; font-family: inherit; }
        .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(127,119,221,0.4); }
        .btn-ghost { background: rgba(255,255,255,0.05); border: 0.5px solid rgba(255,255,255,0.1); color: #f0efe9; cursor: pointer; transition: all 0.2s; font-family: inherit; }
        .btn-ghost:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); }
        .card-hover { transition: transform 0.2s, box-shadow 0.2s; }
        .card-hover:hover { transform: translateY(-4px); box-shadow: 0 20px 60px rgba(0,0,0,0.4); }
        .feature-card { background: linear-gradient(135deg, rgba(127,119,221,0.06), rgba(29,158,117,0.06)); border: 0.5px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 28px; transition: all 0.3s; }
        .feature-card:hover { border-color: rgba(127,119,221,0.3); background: linear-gradient(135deg, rgba(127,119,221,0.1), rgba(29,158,117,0.1)); transform: translateY(-4px); }
      `}</style>

      {/* Ambient background */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: -200, left: '20%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(127,119,221,0.12) 0%, transparent 70%)', animation: 'glow 4s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: 400, right: '10%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(29,158,117,0.1) 0%, transparent 70%)', animation: 'glow 4s ease-in-out infinite 2s' }} />
      </div>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px', height: 64, borderBottom: '0.5px solid rgba(255,255,255,0.06)', background: 'rgba(8,11,15,0.9)', backdropFilter: 'blur(20px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 20px rgba(127,119,221,0.4)' }}>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: '-1px' }}>F</span>
          </div>
          <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-1px' }}>FLO</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <a href="#how" style={{ fontSize: 14, color: 'rgba(240,239,233,0.6)', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color='#f0efe9'} onMouseLeave={e => e.target.style.color='rgba(240,239,233,0.6)'}>How it works</a>
          <a href="#features" style={{ fontSize: 14, color: 'rgba(240,239,233,0.6)', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color='#f0efe9'} onMouseLeave={e => e.target.style.color='rgba(240,239,233,0.6)'}>Features</a>
          <a href="#pricing" style={{ fontSize: 14, color: 'rgba(240,239,233,0.6)', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color='#f0efe9'} onMouseLeave={e => e.target.style.color='rgba(240,239,233,0.6)'}>Pricing</a>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={() => navigate('/login')} style={{ padding: '8px 20px', borderRadius: 8, fontSize: 14, fontWeight: 500 }}>Log in</button>
          <button className="btn-primary" onClick={() => navigate('/signup')} style={{ padding: '8px 20px', borderRadius: 8, fontSize: 14 }}>Start free trial →</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '120px 24px 80px' }}>
        <AnimatedSection>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 100, border: '0.5px solid rgba(127,119,221,0.4)', background: 'rgba(127,119,221,0.08)', marginBottom: 32 }}>
            <PulsingDot color="#7F77DD" />
            <span style={{ fontSize: 13, color: '#AFA9EC', fontWeight: 500 }}>Free to start — pay only for messages</span>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={100}>
          <h1 style={{ fontSize: 'clamp(44px, 7vw, 82px)', fontWeight: 800, letterSpacing: '-3px', lineHeight: 1, marginBottom: 28, maxWidth: 900, margin: '0 auto 28px' }}>
            Your AI insurance<br />
            <span style={{ ...GRADIENT_TEXT }}>sales assistant</span>
          </h1>
        </AnimatedSection>

        <AnimatedSection delay={200}>
          <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'rgba(240,239,233,0.55)', maxWidth: 560, margin: '0 auto 44px', lineHeight: 1.8, fontWeight: 400 }}>
            FLO texts your leads, handles every reply with AI 24/7, and books appointments — automatically. Your only job is to show up and close.
          </p>
        </AnimatedSection>

        <AnimatedSection delay={300}>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 60 }}>
            <button className="btn-primary" onClick={() => navigate('/signup')} style={{ padding: '14px 36px', borderRadius: 12, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              Start free trial
              <span style={{ fontSize: 18 }}>→</span>
            </button>
            <button className="btn-ghost" onClick={() => document.getElementById('how').scrollIntoView({ behavior: 'smooth' })} style={{ padding: '14px 28px', borderRadius: 12, fontSize: 16 }}>
              See how it works
            </button>
          </div>
        </AnimatedSection>

        {/* Live stats */}
        <AnimatedSection delay={400}>
          <div style={{ display: 'inline-flex', gap: 48, padding: '20px 40px', borderRadius: 16, background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
            {[
              { label: 'Leads contacted', value: count.leads.toLocaleString(), color: '#7F77DD' },
              { label: 'Texts sent', value: count.texts.toLocaleString(), color: '#5BA8DD' },
              { label: 'Appointments booked', value: count.appts.toLocaleString(), color: '#1D9E75' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: s.color, letterSpacing: '-1px', fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
                <div style={{ fontSize: 12, color: 'rgba(240,239,233,0.4)', marginTop: 4, fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </section>

      {/* How it works */}
      <section id="how" style={{ position: 'relative', zIndex: 1, padding: '100px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <AnimatedSection style={{ textAlign: 'center', marginBottom: 64 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '3px', color: '#7F77DD', textTransform: 'uppercase', marginBottom: 16 }}>THE FLOW</div>
          <h2 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 800, letterSpacing: '-2px', marginBottom: 16 }}>4 steps. Zero manual work.</h2>
          <p style={{ fontSize: 16, color: 'rgba(240,239,233,0.5)', maxWidth: 500, margin: '0 auto' }}>From stranger to booked appointment — fully automated.</p>
        </AnimatedSection>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2 }}>
          {STEPS.map((step, i) => (
            <AnimatedSection key={step.n} delay={i * 100}>
              <div style={{ padding: '32px 28px', background: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent', borderRadius: 0, borderTop: `2px solid ${step.color}`, position: 'relative' }}>
                <div style={{ fontSize: 48, fontWeight: 800, color: step.color, opacity: 0.15, letterSpacing: '-3px', lineHeight: 1, marginBottom: 16, fontVariantNumeric: 'tabular-nums' }}>{step.n}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#f0efe9', letterSpacing: '-0.5px', marginBottom: 12 }}>{step.title}</div>
                <div style={{ fontSize: 14, color: 'rgba(240,239,233,0.5)', lineHeight: 1.7 }}>{step.desc}</div>
                {i < STEPS.length - 1 && (
                  <div style={{ position: 'absolute', right: -14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.15)', fontSize: 20, zIndex: 2, display: 'none' }}>→</div>
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Feature highlight */}
      <section style={{ position: 'relative', zIndex: 1, padding: '80px 24px', background: 'linear-gradient(180deg, transparent, rgba(127,119,221,0.05), transparent)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
          <AnimatedSection>
            <h2 style={{ fontSize: 'clamp(30px, 3.5vw, 46px)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.1, marginBottom: 20 }}>
              Your pipeline,<br /><span style={{ ...GRADIENT_TEXT }}>always moving.</span>
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(240,239,233,0.55)', lineHeight: 1.8, marginBottom: 28 }}>
              FLO works your leads around the clock — texting, qualifying, and booking appointments while you focus on closing. Every plan includes the full AI.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {['Texts every new lead automatically', 'AI handles all replies and objections', 'Appointments booked and confirmed automatically', 'Pro and Pro+ unlock lead scraper access on sladeiq.com'].map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'rgba(240,239,233,0.7)' }}>
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(29,158,117,0.2)', border: '0.5px solid #1D9E75', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11, color: '#1D9E75' }}>✓</span>
                  {f}
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: 28, animation: 'float 6s ease-in-out infinite' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '10px 14px', borderRadius: 10, background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.3)' }}>
                <PulsingDot color="#1D9E75" />
                <span style={{ fontSize: 13, color: '#4ade80', fontWeight: 600 }}>24/7 Engine — RUNNING</span>
              </div>
              {[
                { time: '9:02 AM', msg: 'Texted Marco at Pro Painting LLC', color: '#5BA8DD' },
                { time: '9:47 AM', msg: 'Marco replied: "Yes I\'m interested"', color: '#f0efe9' },
                { time: '9:48 AM', msg: 'Sent quote range — $250–$350/mo for his ZIP', color: '#7F77DD' },
                { time: '10:12 AM', msg: 'Appointment booked with Marco — 2pm Thu', color: '#1D9E75' },
                { time: '2:31 PM', msg: 'Texted 14 new leads — Tampa contractors', color: '#5BA8DD' },
              ].map((log, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '0.5px solid rgba(255,255,255,0.05)', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 11, color: 'rgba(240,239,233,0.3)', fontFamily: 'DM Mono, monospace', flexShrink: 0, paddingTop: 1 }}>{log.time}</span>
                  <span style={{ fontSize: 13, color: log.color, lineHeight: 1.4 }}>{log.msg}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" style={{ position: 'relative', zIndex: 1, padding: '100px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <AnimatedSection style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '3px', color: '#7F77DD', textTransform: 'uppercase', marginBottom: 16 }}>FEATURES</div>
          <h2 style={{ fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 800, letterSpacing: '-2px' }}>Everything you need to scale.</h2>
        </AnimatedSection>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          {FEATURES.map((f, i) => (
            <AnimatedSection key={f.title} delay={i * 80}>
              <div className="feature-card">
                <div style={{ fontSize: 28, marginBottom: 14 }}>{f.icon}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#f0efe9', letterSpacing: '-0.3px' }}>{f.title}</span>
                  {f.badge && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: 'rgba(29,158,117,0.15)', color: '#4ade80', letterSpacing: '0.5px' }}>{f.badge}</span>}
                </div>
                <p style={{ fontSize: 14, color: 'rgba(240,239,233,0.5)', lineHeight: 1.7 }}>{f.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ position: 'relative', zIndex: 1, padding: '100px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <AnimatedSection style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '3px', color: '#7F77DD', textTransform: 'uppercase', marginBottom: 16 }}>PRICING</div>
          <h2 style={{ fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 800, letterSpacing: '-2px', marginBottom: 14 }}>Simple, transparent pricing.</h2>
          <p style={{ fontSize: 16, color: 'rgba(240,239,233,0.5)' }}>Free forever on Basic. Upgrade anytime for scraper access.</p>
        </AnimatedSection>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {PLANS.map((plan, i) => (
            <AnimatedSection key={plan.name} delay={i * 100}>
              <div className="card-hover" style={{ height: '100%', background: plan.highlight ? 'linear-gradient(135deg, rgba(127,119,221,0.15), rgba(29,158,117,0.15))' : 'rgba(255,255,255,0.03)', border: plan.highlight ? '1px solid rgba(127,119,221,0.5)' : '0.5px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: 32, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
                {plan.highlight && (
                  <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: GRADIENT, color: '#fff', letterSpacing: '0.5px' }}>MOST POPULAR</div>
                )}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(240,239,233,0.5)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '1px' }}>{plan.name}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 10 }}>
                    <span style={{ fontSize: 48, fontWeight: 800, letterSpacing: '-3px', color: '#f0efe9' }}>{plan.price}</span>
                    <span style={{ fontSize: 14, color: 'rgba(240,239,233,0.4)' }}>/mo</span>
                  </div>
                  <p style={{ fontSize: 14, color: 'rgba(240,239,233,0.5)', lineHeight: 1.6 }}>{plan.desc}</p>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
                  {plan.features.map(f => (
                    <div key={f} style={{ display: 'flex', gap: 10, fontSize: 14, color: 'rgba(240,239,233,0.75)', alignItems: 'flex-start' }}>
                      <span style={{ color: '#1D9E75', flexShrink: 0, marginTop: 1 }}>✓</span>
                      {f}
                    </div>
                  ))}
                </div>
                <button className={plan.highlight ? 'btn-primary' : 'btn-ghost'} onClick={() => navigate('/signup')} style={{ padding: '12px 24px', borderRadius: 10, fontSize: 15, fontWeight: 600, width: '100%' }}>
                  {plan.cta}
                </button>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ position: 'relative', zIndex: 1, padding: '100px 24px', textAlign: 'center' }}>
        <AnimatedSection>
          <div style={{ maxWidth: 700, margin: '0 auto', padding: '60px 40px', borderRadius: 24, background: 'linear-gradient(135deg, rgba(127,119,221,0.12), rgba(29,158,117,0.12))', border: '0.5px solid rgba(127,119,221,0.25)' }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, letterSpacing: '-2px', marginBottom: 16 }}>
              Ready to run your sales<br />on autopilot 24/7?
            </h2>
            <p style={{ fontSize: 16, color: 'rgba(240,239,233,0.5)', marginBottom: 36, lineHeight: 1.7 }}>
              Join insurance agents using FLO to fill their pipeline automatically. Start your 14-day free trial today.
            </p>
            <button className="btn-primary" onClick={() => navigate('/signup')} style={{ padding: '16px 48px', borderRadius: 12, fontSize: 18, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              Start free trial — it's free
              <span style={{ fontSize: 20 }}>→</span>
            </button>
          </div>
        </AnimatedSection>
      </section>

      {/* Contact */}
      <section id="contact" style={{ position: 'relative', zIndex: 1, padding: '100px 24px', maxWidth: 680, margin: '0 auto' }}>
        <AnimatedSection style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '3px', color: '#7F77DD', textTransform: 'uppercase', marginBottom: 16 }}>CONTACT</div>
          <h2 style={{ fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 800, letterSpacing: '-2px', marginBottom: 14 }}>Talk to FLO</h2>
          <p style={{ fontSize: 16, color: 'rgba(240,239,233,0.5)', lineHeight: 1.7 }}>Send a message and our AI will respond instantly.</p>
        </AnimatedSection>

        <AnimatedSection delay={100}>
          <ChatWidget />
        </AnimatedSection>
      </section>

      {/* Footer */}
      <footer style={{ position: 'relative', zIndex: 1, padding: '32px 48px', borderTop: '0.5px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>F</span>
          </div>
          <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.5px' }}>FLO</span>
        </div>
        <span style={{ fontSize: 13, color: 'rgba(240,239,233,0.3)' }}>© 2026 FLO. AI-powered insurance sales.</span>
        <div style={{ display: 'flex', gap: 20 }}>
          {[
            { label: 'Privacy', href: '/privacy' },
            { label: 'Terms', href: '/terms' },
            { label: 'Contact', href: '#contact' },
          ].map(l => (
            <a key={l.label} href={l.href} style={{ fontSize: 13, color: 'rgba(240,239,233,0.35)', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color='#f0efe9'} onMouseLeave={e => e.target.style.color='rgba(240,239,233,0.35)'}>{l.label}</a>
          ))}
        </div>
      </footer>
    </div>
  );
}